import React, { useState, useRef, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Grid, Typography, Select, MenuItem, InputLabel, FormControl, OutlinedInput, Checkbox, ListItemText } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Form from "./Form.jsx";
import {deviceNames} from '../../app/DeviceSelector.tsx'

const expirationOptions = [
  { value: "never", label: "Never" },
  { value: "1/month", label: "1 month" },
  { value: "2/months", label: "2 months" },
  { value: "3/months", label: "3 months" },
  { value: "4/months", label: "4 months" },
  { value: "5/months", label: "5 months" },
  { value: "6/months", label: "6 months" },
  { value: "8/months", label: "8 months" },
  { value: "10/months", label: "10 months" },
  { value: "12/months", label: "12 months" },
  { value: "14/months", label: "14 months" },
  { value: "16/months", label: "16 months" },
  { value: "18/months", label: "18 months" },
  { value: "20/months", label: "20 months" },
  { value: "22/months", label: "22 months" },
  { value: "24/months", label: "24 months" },
];

const skuOptions = [
  { value: 1, label: "Uroginecology" },
  { value: 2, label: "RTA (Return to Activity)" },
];

const packageOptions = [
  "neurologic",
  "orthopedic",
  "pain",
  "pelvic",
  "sport"
];

const modeOptions = [
  { value: "sku", label: "SKU" },
  { value: "classification", label: "Classification packages" }
];

export default function FormLicenceExoClinic({ deviceType }) {
  const [validationError, setValidationError] = useState("");
  const [mode, setMode] = useState("classification");
  const [expirationDate, setExpirationDate] = useState("never");
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [selectedSku, setSelectedSku] = useState(0);
  const [serialNumber, setSerialNumber] = useState("");
  const [licenceNumber, setLicenceNumber] = useState("");
  const [hasError, setHasError] = useState("");
  const licenseNumberInput = useRef(null);
  const licenseNumberContainer = useRef(null);
  const history = useHistory();

  useEffect(() => {
    if (!deviceType) {
      history.push("/");
    }
  }, [deviceType, history]);

  const handleModeChange = (e) => setMode(e.target.value);
  const handleExpirationChange = (e) => setExpirationDate(e.target.value);
  const handleSkuChange = (e) => setSelectedSku(e.target.value);
  const handlePackagesChange = (e) => setSelectedPackages(e.target.value);
  const handleSerialChange = (e) => setSerialNumber(e.target.value);

  const handleClickButton = async (event) => {
    event.preventDefault();
    // Validate required fields
    if ((mode === "classification" && selectedPackages.length === 0) || serialNumber.trim() === "") {
      setValidationError("License was not generated. Please fill out the form correctly.");
      return;
    }
    setValidationError("");
    try {
      // http for testing with local backend, https for production
      const backendProtocol = window.location.href.startsWith("https") ? "https" : "http";
      // const backendUrl = `${backendProtocol}://localhost:3000/api/generate-licence-exo-clinic`;
      const backendUrl = `${backendProtocol}://license-generator-backend.internal.egzotech.com/api/generate-licence-exo-clinic`;
      const body = {
        licenseName: mode,
        expiresPeriod: expirationDate,
        serialNumber: serialNumber
      };
      if (mode === 'sku') {
        body.sku = selectedSku;
      }
      if (mode === "classification") {
        body.packages = selectedPackages;
      }
      const response = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      setLicenceNumber(data.licenceKey || "");
      setHasError("");
    } catch (error) {
      setHasError("Error generating licence");
    }
  };

  const handleCopyButton = useCallback(() => {
    if (!licenceNumber) return;
    navigator.clipboard.writeText(licenceNumber);
  }, [licenceNumber]);

  const handleSelectAllText = useCallback(() => {
    licenseNumberInput.current.select();
  }, []);

  const handleCleanForm = () => {
  setExpirationDate("never");
  setSelectedPackages([]);
  setSerialNumber("");
  setLicenceNumber("");
  setHasError("");
  setMode("classification");
  };

  // Responsive maxWidth using Material-UI useMediaQuery
  const isLarge = useMediaQuery("(min-width:1024px)");
  const maxWidth = isLarge ? 900 : 600;
  return (
    <Box style={{ maxWidth, margin: "0 auto", marginTop: 32 }}>
      <Box mb={2} display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h6" style={{ fontWeight: "bold" }}>
          Selected device: {deviceNames[deviceType]}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          style={{ marginTop: 12 }}
          onClick={() => history.push("/")}
        >
          Back to device selection
        </Button>
      </Box>
      <Form>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>License Type</InputLabel>
              <Select
                value={mode}
                onChange={handleModeChange}
                label="License Type"
              >
                {modeOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Expiration date</InputLabel>
              <Select
                value={expirationDate}
                onChange={handleExpirationChange}
                label="Expiration date"
              >
                {expirationOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {mode === "sku" && (
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Choose SKU</InputLabel>
                <Select
                  value={selectedSku}
                  onChange={handleSkuChange}
                  input={<OutlinedInput label="Packages" />}
                  renderValue={(selected) => skuOptions.find(pkg => pkg.value === selected)?.label || ""}
                >
                  {skuOptions.map((pkg) => (
                    <MenuItem key={pkg.label} value={pkg.value}>
                      <ListItemText primary={pkg.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {mode === "classification" && (
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Packages</InputLabel>
                <Select
                  multiple
                  value={selectedPackages}
                  onChange={handlePackagesChange}
                  input={<OutlinedInput label="Packages" />}
                  renderValue={(selected) => selected.join(", ")}
                >
                  {packageOptions.map((pkg) => (
                    <MenuItem key={pkg} value={pkg}>
                      <Checkbox checked={selectedPackages.indexOf(pkg) > -1} />
                      <ListItemText primary={pkg} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <InputLabel>Serial Number</InputLabel>
            <OutlinedInput
              fullWidth
              value={serialNumber}
              onChange={handleSerialChange}
              error={!!hasError}
            />
          </Grid>
          <Grid item xs={12} style={{ display: 'flex', gap: 16, flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 16 }}>
              <Button
                variant="contained"
                color="primary"
                style={{ marginTop: 16 }}
                onClick={handleClickButton}
              >
                Generate Key
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                style={{ marginTop: 16 }}
                onClick={handleCleanForm}
              >
                Clean Form
              </Button>
            </div>
            {validationError && (
              <Typography style={{ color: 'red', marginTop: 8 }}>
                {validationError}
              </Typography>
            )}
          </Grid>
          {licenceNumber && (
            <Grid item xs={12}>
              <div ref={licenseNumberContainer} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
                <OutlinedInput
                  readOnly
                  value={licenceNumber}
                  type="text"
                  onClick={handleSelectAllText}
                  inputRef={licenseNumberInput}
                  margin="dense"
                  style={{ width: "100%" }}
                />
                <Button variant="outlined" onClick={handleCopyButton} style={{ marginTop: 8 }}>Copy License</Button>
              </div>
              {hasError && <Typography color="error">{hasError}</Typography>}
            </Grid>
          )}
        </Grid>
      </Form>
    </Box>
  );
}
